// src/resources/reports/ReportPushNotifications/MarketBenchmark.tsx
//
// Régua de mercado (§3 item 4 do spec docs/superpowers/specs/2026-09-18-abertura-influenciada-push-design.md,
// pontuax-backend): sem isso o dono do posto não tem como saber se a taxa de
// leitura dele é boa ou ruim — foi exatamente a pergunta que motivou esta
// entrega. Barra simples com as marcas do benchmark (Airship) + o ponto da
// rede, em vez de gráfico: a régua é olhada uma vez, não acompanhada ao vivo.
import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  BENCHMARK_TIER_LABEL, blendedOpenRateBenchmark, classifyOpenRate, fmtPctSmall,
  PUSH_OPEN_RATE_BENCHMARKS_2025,
} from './types';

const INK = '#0f172a';
const TRACK = '#e2e8f0';
const MARK = '#94a3b8';
const OURS = '#3b82f6';

const MarketBenchmark = ({ readRate }: { readRate: number | null }) => {
  const bench = useMemo(() => blendedOpenRateBenchmark(), []);
  const tier = classifyOpenRate(readRate);

  // Teto da régua: o maior corte do benchmark (p90 geral) com folga, ou a nossa taxa se ela
  // estourar o benchmark — senão o ponto da rede escaparia da barra.
  const axisMax = Math.max(bench.p90 * 1.15, (readRate ?? 0) * 1.1);
  const toPct = (v: number) => `${Math.min(100, Math.max(0, (v / axisMax) * 100))}%`;

  const marks: { key: string; value: number; label: string }[] = [
    { key: 'p10', value: bench.p10, label: 'p10' },
    { key: 'median', value: bench.median, label: 'mediana' },
    { key: 'p90Retail', value: bench.p90Retail, label: 'topo varejo' },
    { key: 'p90', value: bench.p90, label: 'p90' },
  ];

  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2, p: 2, mb: 2 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Onde sua rede está no mercado</Typography>
      <Typography sx={{ fontSize: 12, color: '#64748b', mb: 2 }}>
        Taxa de leitura ({fmtPctSmall(readRate)}) comparada com o benchmark de push do setor — mesma base de cálculo (sobre enviadas).
        {tier && <> <b style={{ color: INK }}>{BENCHMARK_TIER_LABEL[tier](fmtPctSmall(bench.median))}</b>.</>}
      </Typography>

      {readRate === null ? (
        <Typography variant="body2" color="text.secondary">Sem envio suficiente no período pra comparar.</Typography>
      ) : (
        <Box sx={{ px: 1, pb: 1 }}>
          <Box sx={{ position: 'relative', height: 8, borderRadius: 999, bgcolor: TRACK, mt: 3, mx: 1 }}>
            {marks.map(m => (
              <Box key={m.key} sx={{ position: 'absolute', left: toPct(m.value), top: -18, transform: 'translateX(-50%)', textAlign: 'center', width: 70 }}>
                <Typography sx={{ fontSize: 9.5, color: MARK, whiteSpace: 'nowrap' }}>{m.label}</Typography>
                <Box sx={{ width: 2, height: 14, bgcolor: MARK, mx: 'auto', mt: 0.25 }} />
              </Box>
            ))}
            {/* Ponto da rede — por cima das marcas do benchmark, cor de destaque pra achar rápido. */}
            <Box
              sx={{
                position: 'absolute', left: toPct(readRate), top: -6, transform: 'translateX(-50%)',
                width: 20, height: 20, borderRadius: 999, bgcolor: OURS, border: '3px solid #fff',
                boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
              }}
              title={`Sua rede: ${fmtPctSmall(readRate)}`}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography sx={{ fontSize: 10.5, color: MARK }}>0%</Typography>
            <Typography sx={{ fontSize: 10.5, color: MARK }}>{fmtPctSmall(axisMax)}</Typography>
          </Box>
        </Box>
      )}

      <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 1 }}>
        Fonte: {PUSH_OPEN_RATE_BENCHMARKS_2025.source} · média Android/iOS, calculada sobre enviadas.
      </Typography>
    </Box>
  );
};

export default MarketBenchmark;
